import ReactSelect, { createFilter } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import type {
  ActionMeta,
  ClassNamesConfig,
  FormatOptionLabelMeta,
  GroupBase,
  MultiValue as MV,
  SingleValue as SV,
} from 'react-select';
import type { Option, SelectProps } from './Select.types';

const baseClasses = ['tw:focus:ring-0', 'tw:disabled:cursor-not-allowed'].join(' ');

const containerStyles = ['tw:bg-sq-white', 'tw:dark:bg-sq-dark-background'].join(' ');

const errorClasses = 'tw:border-sq-danger';

const borderColorClasses = ['tw:border-sq-disabled-gray', 'tw:dark:border-sq-dark-disabled-gray'].join(' ');

const borderStyles = ['tw:border-solid', 'tw:border'].join(' ');

const borderActiveClasses = [
  'tw:hover:border-sq-theme-dark',
  'tw:focus-within:border-sq-theme-dark',
  'tw:active:border-sq-theme-dark',
  'tw:dark:hover:border-sq-theme-dark-dark',
  'tw:dark:focus-within:border-sq-theme-dark-dark',
  'tw:dark:active:border-sq-theme-dark-dark',
].join(' ');

const textStyles = ['tw:text-sq-text-color', 'tw:dark:text-sq-dark-text', 'tw:text-sm'].join(' ');

export type MultiValue<T> = MV<T>;
export type SingleValue<T> = SV<T>;

const textActiveStyles = [
  'tw:hover:text-sq-theme-dark',
  'tw:focus:text-sq-theme-dark',
  'tw:active:text-sq-theme-dark',
  'tw:dark:text-sq-dark-disabled-gray',
  'tw:dark:hover:text-sq-theme-dark-dark',
  'tw:dark:focus:text-sq-theme-dark-dark',
  'tw:dark:active:text-sq-theme-dark-dark',
  'tw:hover:text-sq-theme-dark',
  'tw:dark:text-sq-dark-disabled-gray',
  'tw:dark:hover:text-sq-theme-dark-dark',
].join(' ');

const menuStyles = [
  'tw:border-solid',
  'tw:border',
  'tw:rounded-b',
  'tw:border-sq-disabled-gray',
  'tw:dark:border-sq-dark-disabled-gray',
  'tw:whitespace-nowrap',
  'tw:min-w-fit',
  'tw:z-[9999]!',
].join(' ');

const menuListStyles = [
  'tw:rounded-b',
  'tw:bg-sq-white',
  'tw:dark:bg-sq-dark-background',
  'tw:min-w-fit',
  'tw:z-[9999]!',
  'specSelectMenu',
].join(' ');

const groupHeadingStyles = [
  'tw:bg-sq-light-gray',
  'tw:dark:bg-sq-dark-disabled-gray',
  'tw:text-sq-darkish-gray',
  'tw:py-1',
  'tw:px-2.5',
  'specSelectGroupHeading',
].join(' ');

const optionStyles = [
  'tw:hover:bg-sq-gray-highlight',
  'tw:hover:cursor-pointer',
  'tw:dark:hover:bg-sq-gray-highlight-dark',
  'tw:py-1',
  'tw:px-2.5',
  'specSelectOption',
].join(' ');

const disabledFieldClasses = [
  'tw:bg-sq-light-gray',
  'tw:dark:bg-sq-dark-disabled-gray',
  'tw:text-sq-darkish-gray',
  'tw:dark:text-sq-dark-text/30',
  'tw:cursor-not-allowed!',
].join(' ');

const dropDownIndicatorStyles = `tw:text-sq-disabled-gray ${textActiveStyles} specOpenIt`;

const placeholderStyles = ['tw:text-gray-400', 'tw:dark:text-sq-dark-text-lighter', 'specOpenSelect'].join(' ');

const multiValueStyles = [
  'tw:bg-sq-disabled-gray',
  'tw:dark:bg-sq-multi-gray-dark',
  'tw:text-sm',
  'tw:py-0.5',
  'tw:px-1',
  'tw:m-0.5',
  'tw:rounded-sm',
  'specOpenSelect',
].join(' ');

/**
 * Select Component
 *  - based on react-select (https://react-select.com/)
 *
 *  For ease of testing most of the elements of this select can be identified by classNames prefixed with "spec".
 *
 */
const Select = ({
  options,
  value,
  placeholder = 'select',
  noOptionsMessage = 'no matching options',
  isSearchable = true,
  creatable = false,
  isMulti = false,
  isClearable = false,
  closeMenuOnSelect = true,
  id,
  inputId,
  menuPortalTarget,
  getOptionLabel,
  getSelectedValueLabel,
  getOptionValue,
  onChange,
  menuIsOpen,
  menuPlacement = 'auto',
  extraClassNames,
  controlClassNames = '',
  inputGroup,
  filterConfig,
  filterOption,
  small = false,
  isDisabled = false,
  showError = false,
  isLoading = false,
  formatGroupLabel,
  onRemove,
  defaultValue,
  onMenuOpen,
  onMenuClose,
  components,
  onMenuInputFocus,
  onInputChange,
  inputValue,
  onMenuScrollToBottom,
}: SelectProps) => {
  const getOptionOrSelectedLabel = (option: Option, { context }: FormatOptionLabelMeta<Option>) => {
    const getSelectedOptionLabel = getSelectedValueLabel ? getSelectedValueLabel : getOptionLabel;

    if (getOptionLabel || getSelectedValueLabel) {
      if (context === 'value') {
        return getSelectedOptionLabel ? getSelectedOptionLabel(option) : option.label;
      } else {
        return getOptionLabel ? getOptionLabel(option) : option.label;
      }
    } else {
      return option.label;
    }
  };

  const interceptOnChange = (newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => {
    if (isMulti && onRemove && actionMeta.action === 'remove-value') {
      onRemove({ id: actionMeta.removedValue });
      return;
    }
    onChange(newValue);
  };

  // react-select defaults blurInputOnSelect to true on touch-capable devices; that can close the menu even when
  // closeMenuOnSelect is false. Only make `blurInputOnSelect=false` when `closeMenuOnSelect=false` (e.g., multi-select
  // where the menu should stay open after each pick), otherwise set to `undefined` to default to the built-in
  // react-select behaviour.
  const blurInputOnSelect = closeMenuOnSelect ? undefined : false;

  const props = {
    id,
    value,
    inputId,
    placeholder,
    closeMenuOnSelect,
    blurInputOnSelect,
    isSearchable: creatable ? true : isSearchable,
    isMulti,
    isClearable,
    isLoading,
    defaultValue,
    noOptionsMessage: () => noOptionsMessage,
    menuPortalTarget,
    menuIsOpen,
    onChange: interceptOnChange,
    unstyled: true,
    menuPlacement,
    formatOptionLabel: getOptionOrSelectedLabel,
    formatGroupLabel,
    getOptionValue,
    options,
    isDisabled,
    onMenuOpen,
    onMenuClose,
    components,
    onMenuInputFocus,
    onInputChange,
    inputValue,
    onMenuScrollToBottom,
    hideSelectedOptions: isMulti,
    filterOption: filterOption ? filterOption : filterConfig ? createFilter(filterConfig) : undefined,
    classNames: {
      control: ({ menuIsOpen }) => {
        let border = menuIsOpen ? 'tw:rounded-t-md' : 'tw:rounded-md';

        // if it's on the left side of the input group make sure the right side is straight and not curved
        if (inputGroup === 'left') {
          border = menuIsOpen ? 'tw:rounded-tl-md' : 'tw:rounded-l-md';
        } else if (inputGroup === 'right') {
          border = menuIsOpen ? 'tw:rounded-tr-md' : 'tw:rounded-r-md';
        }
        const appliedClasses = `${borderStyles} ${isDisabled ? '' : borderActiveClasses} ${border}  ${
          showError ? errorClasses : borderColorClasses
        } ${small ? 'reactSelectMinHeightSmall' : 'reactSelectMinHeight'} ${controlClassNames}`;
        return `${appliedClasses} ${baseClasses} ${isDisabled ? '' : containerStyles} specSelectControl ${
          isDisabled ? disabledFieldClasses : ''
        } ${small ? 'tw:pl-2 tw:pr-1' : 'tw:pl-2.5 tw:pr-1.5'}`;
      },
      placeholder: () => placeholderStyles,
      container: () => {
        return `${textStyles} ${extraClassNames} tw:pointer-events-auto!`;
      },
      input: () => `${textStyles} specSelectInput`,
      menuList: () => menuListStyles,
      menu: () => menuStyles,
      menuPortal: () => 'tw:z-[9000]!',
      dropdownIndicator: () => dropDownIndicatorStyles,
      option: ({ isSelected, isDisabled, isFocused }) => {
        let classes = optionStyles;
        if (isDisabled) {
          classes += ' specDisabledOption ';
        }
        if (isSelected) {
          return `${classes} tw:bg-sq-colored-hover tw:dark:bg-sq-colored-hover-dark`;
        }
        return isFocused ? `${classes} tw:bg-sq-gray-highlight tw:dark:bg-sq-gray-highlight-dark` : classes;
      },
      singleValue: () => 'specOpenSelect',
      multiValue: () => multiValueStyles,
      multiValueRemove: () => 'tw:hover:text-sq-danger specSelectMultiValueRemove',
      clearIndicator: () => 'tw:text-sq-disabled-gray tw:hover:text-sq-danger specClearSelect',
      group: () => 'specSelectGroup',
      groupHeading: () => groupHeadingStyles,
    } satisfies ClassNamesConfig<Option, boolean, GroupBase<Option>>,
  };

  return creatable ? <CreatableSelect {...props} /> : <ReactSelect {...props} />;
};
export default Select;
